import {
  BillableCalculationEnum,
  EmployerContractFragment as EmployerContract,
  EmployerContractModificationFragment as EmployerContractModification
} from "@typing/Generated";

type ContractDetails = {
  additionalPackageDetails: string | null;
  billableCalculation: BillableCalculationEnum | null;
  billableCap: number | null;
  employeesPurchased: number | null;
  hasCarePlanning: boolean;
  hasUnlimitedMinutes: boolean;
  minutes: number;
  packageCost: string | null;
  paymentTerms: string | null;
  platformFee: string | null;
};

const contractModificationUpdateFields = (contractModification: EmployerContractModification) => {
  const updateFields = [];

  if (contractModification.changesBillableCalculation) {
    updateFields.push("billableCalculation");
  }
  if (contractModification.changesBillableCap) {
    updateFields.push("billableCap");
  }
  if (contractModification.changesContractDetails) {
    updateFields.push("additionalPackageDetails");
    updateFields.push("packageCost");
    updateFields.push("paymentTerms");
    updateFields.push("platformFee");
  }
  if (contractModification.changesEmployeesPurchased) {
    updateFields.push("employeesPurchased");
  }
  if (contractModification.changesPackage) {
    updateFields.push("hasCarePlanning");
    updateFields.push("hasUnlimitedMinutes");
    updateFields.push("minutes");
  }

  return updateFields;
};

export const sortedContractModifications = <T extends EmployerContractModification>(contractModifications: T[]) =>
  [...contractModifications].sort((a, b) => {
    if (a.startDate.getTime() === b.startDate.getTime()) {
      return a.createdAt > b.createdAt ? 1 : -1;
    }

    return a.startDate > b.startDate ? 1 : -1;
  });

export const contractDetailsOnDate = (contract: EmployerContract, date: Date): ContractDetails => {
  const answer: ContractDetails = { ...contract };

  if (contract.employerContractModifications) {
    const filteredModifications = contract.employerContractModifications.filter(mod => mod.startDate < date);

    sortedContractModifications(filteredModifications).forEach(mod => {
      const updateFields = contractModificationUpdateFields(mod);

      updateFields.forEach(field => {
        if ((mod as any)[field] !== null) {
          (answer as any)[field] = (mod as any)[field];
        }
      });
    });
  }

  return answer;
};

type ModificationChanges = {
  [key: string]: { new: any; old: any };
};

export const contractModificationChanges = (
  contract: EmployerContract,
  contractModification: EmployerContractModification
): ModificationChanges => {
  const thingToCompare: ContractDetails = { ...contract };

  if (contract.employerContractModifications) {
    const sortedModifications = sortedContractModifications(contract.employerContractModifications);
    const modificationIndex = sortedModifications.indexOf(contractModification);

    const previousMods = sortedModifications.splice(0, modificationIndex);

    previousMods.forEach(previousMod => {
      const updateFields = contractModificationUpdateFields(previousMod);

      updateFields.forEach(field => {
        if ((previousMod as any)[field] !== null) {
          (thingToCompare as any)[field] = (previousMod as any)[field];
        }
      });
    });
  }

  const delta: ModificationChanges = {};

  const updateFields = contractModificationUpdateFields(contractModification);

  updateFields.forEach(key => {
    if ((contractModification as any)[key] !== (thingToCompare as any)[key]) {
      delta[key] = { new: (contractModification as any)[key], old: (thingToCompare as any)[key] };
    }
  });

  return delta;
};
